import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronRight } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Table as TablePrimitive } from '@/_shared/components/ui/table';
import { coerceLabel } from '@/_shared/lib/coerce-label';
import { cn } from '@/_shared/lib/utils';

import { TableHeaderRow } from './TableHeaderRow';
import { TableRows } from './TableRows';
import type { TableColumnMeta, TableProps } from './types';
import { defaultCell, resolveSorter, rowValue, toCsv } from './utils';

/** Default single-line row height; callers with taller rows pass `estimateRowHeight`. */
const ESTIMATED_ROW_HEIGHT = 33;
const AUTO_VIRTUALIZE_THRESHOLD = 100;
const HEADER_ROW_COUNT = 1;

/**
 * The table *renderer*: TanStack Table (core + optional sort) drawn through the
 * shadcn primitive, with a height-restricted scroll viewport and row virtualization.
 */
export function Table<T>({
  data,
  columns,
  getRowId,
  title,
  enableCsvExport = false,
  hardenCsvExport = false,
  showRowCount = false,
  totalRows = null,
  enableSort = false,
  virtualize: virtualizeProp,
  estimateRowHeight = ESTIMATED_ROW_HEIGHT,
  scrollElementRef,
  emptyState = 'No results',
  rowClassName,
  onRowClick,
  selectedRowId = null,
  maxHeight = '32rem',
  fillHeight = false,
  resetScrollOnDataChange = true,
  hideHeader = false,
  fixedLayout = false,
  bordered = true,
  className,
}: TableProps<T>): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const external = scrollElementRef != null;

  // Only the EXTERNAL ref needs resolving through state: it belongs to an ancestor, and React attaches a
  // parent's ref after its children's layout effects run, so at mount it is still null and the virtualizer
  // would measure a zero viewport. Our OWN ref must be read live — snapshotting it into state made the
  // lookup one-shot, so a table that first rendered with no columns (the early return below skips the
  // viewport) kept a null element forever and rendered zero rows.
  const [externalScrollEl, setExternalScrollEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!external) return;
    setExternalScrollEl(scrollElementRef?.current ?? null);
  }, [external, scrollElementRef]);
  const getScrollEl = (): HTMLElement | null => (external ? externalScrollEl : scrollRef.current);

  // Distance from the external scroller's content top down to the table — everything above it the
  // virtualizer must offset its window by. Measured from live rects (`offsetTop` is relative to the offset
  // parent, not necessarily the scroller) and re-measured as the scroller resizes or the rows change.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const content = scrollRef.current;
    if (!external || !externalScrollEl || !content) return;
    const measure = (): void => {
      // Rows begin below the header, so discount it — otherwise a table that shows its header windows
      // every row one header-height early.
      const headHeight = content.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      setScrollMargin(
        content.getBoundingClientRect().top -
          externalScrollEl.getBoundingClientRect().top +
          externalScrollEl.scrollTop +
          headHeight,
      );
    };
    measure();
    // Watch the scroller AND its direct children: what shifts the table is content ABOVE it (page
    // sections, the toolbar), which changes a child's height without touching the scroller's own box.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(externalScrollEl);
    for (const child of externalScrollEl.children) resizeObserver.observe(child);
    // Sections above the table can also mount/unmount outright — a childList change no existing box
    // resizes for, so the ResizeObserver never fires. Re-measure and start observing the new children.
    const mutationObserver = new MutationObserver(() => {
      measure();
      for (const child of externalScrollEl.children) resizeObserver.observe(child);
    });
    mutationObserver.observe(externalScrollEl, { childList: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
    // `columns.length` re-runs this after the empty-columns early return finally renders the viewport
    // (`scrollRef` is null on that first pass, so without it the observer would never attach); `data.length`
    // re-measures when the row set changes, matching VirtualizedCards' `items.length` dep.
  }, [external, externalScrollEl, columns.length, data.length]);

  // Snap back to the top when the result set changes (opt-in) — before paint, so a filtered list never
  // flashes at the old scroll offset. Keyed on `data` identity, so the caller must hand over a STABLE array
  // (query data or a `useMemo`); one rebuilt inline per render reads as a new result set on every render.
  // Never for an external scroller: yanking the *page* to the top would rip the user past whatever sits
  // above the table, and a shrinking list clamps the page scroll on its own.
  useLayoutEffect(() => {
    if (resetScrollOnDataChange && !external && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [data, resetScrollOnDataChange, external]);

  const columnDefs = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col, index) => {
        const align = col.align ?? 'left';
        const meta: TableColumnMeta = {
          align,
          headerLabel: col.header,
          headerAriaLabel: col.headerAriaLabel,
          wrap: col.wrap,
        };
        const compare = resolveSorter(col.sorter);

        return {
          /**
           * The column's INTERNAL id, which is not always its row key. TanStack rejects a falsy id
           * outright ("Columns require an id when using an accessorFn"), and that throw escapes to the
           * page-level ErrorBoundary — one unrenderable table blanks the whole route.
           *
           * A hand-authored column list can never hit that: a developer types the key. But `deriveColumns`
           * builds columns from `Object.keys(row)` — warehouse results and agent-authored tables — where
           * `''` is legitimate data: the unnamed leading label column of a comparison table
           * (`{ "": "Before", "Members": 84 }`) is exactly how a human would write one.
           *
           * So the id falls back to the position while the ACCESSOR keeps the real key. The empty header
           * still renders empty, which is what the author meant.
           */
          id: col.id || `col_${index}`,
          accessorFn: (row: T) => rowValue(row, col.id),
          header: col.header,
          enableSorting: col.sortable ?? true,
          sortDescFirst: false,
          sortingFn: (rowA, rowB, columnId) => compare(rowA.getValue(columnId), rowB.getValue(columnId)),
          meta,
          cell: (ctx) => {
            const content = col.cell ? col.cell(ctx.row.original) : defaultCell(ctx.getValue());
            // Rendered in the same cell, but never inside the activator below — see `cellAside`'s doc.
            const aside = col.cellAside?.(ctx.row.original);
            // The primary column doubles as the row's keyboard/SR-operable activator (the row's pointer click is mouse-only).
            if (!col.activatesRow || !onRowClick) {
              return aside == null ? (
                content
              ) : (
                <>
                  {content}
                  {aside}
                </>
              );
            }
            return (
              <>
                <button
                  type="button"
                  aria-label={col.activatesRowLabel ? col.activatesRowLabel(ctx.row.original) : undefined}
                  onClick={() => onRowClick(ctx.row.original, ctx.row.index)}
                  className="flex w-full items-center gap-1 rounded-sm text-left focus-ring"
                >
                  <span className="min-w-0 flex-1">{content}</span>
                  {/* Reveal a "go" caret on row hover/focus; space is reserved so the row never reflows. */}
                  <ChevronRight
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                  />
                </button>
                {aside}
              </>
            );
          },
        };
      }),
    [columns, onRowClick],
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns a mutable instance React Compiler can't memoize; it skips this component safely (TanStack memoizes internally).
  const table = useReactTable<T>({
    data,
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    enableSorting: enableSort,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const virtualize = virtualizeProp ?? data.length > AUTO_VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer<HTMLElement, HTMLTableRowElement>({
    count: rows.length,
    getScrollElement: getScrollEl,
    estimateSize: () => estimateRowHeight,
    overscan: 12,
    scrollMargin,
    enabled: virtualize,
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  // CSV of the currently sorted rows, with raw (unformatted) cell values.
  const csvText = useMemo(() => {
    if (!enableCsvExport) return '';
    const cols = table.getVisibleLeafColumns();
    const headers = cols.map((c) => (c.columnDef.meta as TableColumnMeta | undefined)?.headerLabel ?? c.id);
    // Through `getValue`, NOT `rowValue(row.original, c.id)` — `c.id` is the internal id, which falls
    // back to the position for an unnamed column and would look up a key that isn't there.
    const matrix = rows.map((row) => cols.map((c) => coerceLabel(row.getValue(c.id))));
    return toCsv(headers, matrix, { hardenFormulas: hardenCsvExport });
  }, [enableCsvExport, hardenCsvExport, rows, table]);

  if (columns.length === 0) {
    return <div className={cn('py-8 text-center text-sm text-muted-foreground', className)}>{emptyState}</div>;
  }

  const showHeaderBar = enableCsvExport || title != null;
  const headerRowCount = hideHeader ? 0 : HEADER_ROW_COUNT;
  const hiddenHeaderLabel = hideHeader
    ? columns
        .map((col) => col.headerAriaLabel ?? col.header)
        .filter((label) => label.length > 0)
        .join(', ')
    : undefined;
  const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];
  const totalSize = virtualize ? virtualizer.getTotalSize() : 0;

  const shownCount = rows.length;
  const total = totalRows ?? shownCount;
  const rowCountLabel =
    total > shownCount
      ? `showing ${shownCount} of ${total} rows`
      : `${shownCount} ${shownCount === 1 ? 'row' : 'rows'}`;

  return (
    <div className={cn('flex w-full flex-col', fillHeight && !external && 'h-full', className)}>
      {showHeaderBar && (
        <div className="mb-2 flex shrink-0 items-end justify-between gap-2">
          {title ? (
            <div className="min-h-6 min-w-0 flex-1 text-sm font-medium text-muted-foreground">{title}</div>
          ) : (
            <span />
          )}
          {enableCsvExport && (
            <span className="shrink-0">
              <CopyButton text={csvText} copyLabel="CSV" variant="tertiary" />
            </span>
          )}
        </div>
      )}

      {/* `fillHeight` replaces the fixed `maxHeight` cap with `min(content, available)`, expressed as flex
          SHRINK: the box is content-sized (no `flex-1`, which would stretch a short list into a mostly-empty
          card) and `min-h-0` lets flex squeeze it down to whatever the region leaves. Shrinking — rather
          than a `max-h-full` cap — is what makes it account for the header bar and row-count footer, which
          are siblings in this same column; the pages clip rather than scroll, so overflowing them is
          unreachable. The viewport shrinks with the box and so gets a *definite* used height, which is what
          the virtualizer measures. Horizontal overflow still scrolls in external mode, where the box has no
          height constraint and therefore never grows a vertical scrollbar of its own. */}
      <div
        className={cn(
          'w-full',
          external ? 'overflow-x-auto' : 'overflow-hidden',
          fillHeight && !external && 'flex min-h-0 flex-col',
          bordered && 'rounded-lg border border-border bg-card',
        )}
      >
        <div
          ref={scrollRef}
          className={cn(!external && 'scrollbar-thin overflow-auto', fillHeight && !external && 'min-h-0')}
          style={external || fillHeight ? undefined : { maxHeight }}
        >
          <TablePrimitive
            aria-label={hiddenHeaderLabel}
            aria-rowcount={virtualize ? rows.length + headerRowCount : undefined}
            className={cn(
              'border-separate border-spacing-0 [&_tbody_tr:last-child>td]:border-b-0',
              fixedLayout && 'table-fixed',
            )}
          >
            <colgroup>
              {columns.map((column) => (
                <col key={column.id} style={column.width ? { width: column.width } : undefined} />
              ))}
            </colgroup>

            {!hideHeader && (
              <TableHeaderRow table={table} sorting={sorting} virtualize={virtualize} sticky={!external} />
            )}

            <TableRows
              rows={rows}
              virtualize={virtualize}
              virtualItems={virtualItems}
              totalSize={totalSize}
              measureElement={virtualizer.measureElement}
              visibleColumnCount={visibleColumnCount}
              headerRowCount={headerRowCount}
              scrollMargin={scrollMargin}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
              // Requires `getRowId` to mean anything (`row.id` otherwise falls back to a positional
              // index, which would "match" by coincidence) — without it, do nothing rather than guess.
              selectedRowId={getRowId ? selectedRowId : null}
              emptyState={emptyState}
            />
          </TablePrimitive>
        </div>
      </div>

      {showRowCount && (
        <div className="mt-1.5 shrink-0 text-xs text-muted-foreground" data-testid="row-count">
          {rowCountLabel}
        </div>
      )}
    </div>
  );
}
